import React from 'react'
import { Helmet } from 'react-helmet'
import NavBar from '../components/NavBar'
import EmailSignupButton from '../components/EmailSignupButton'
import Footer from '../components/Footer'
import './SunscreenFacts.css'

const SunscreenFacts = () => {
  return (
    <div className="sunscreen-facts-container">
      <Helmet>
        <title>Sunscreen Facts - Block Automation</title>
        <meta property="og:title" content="Sunscreen Facts - Block Automation" />
        <meta property="og:description" content="Learn why sunscreen is essential for your health and skin." />
      </Helmet>
      
      <NavBar />
      
      <div className="facts-hero-section section-container">
        <div className="max-width">
          <span className="beforeHeading">PROTECT YOUR SKIN</span>
          <h1 className="facts-title">Why You Should Wear Sunscreen</h1>
          <p className="facts-subtitle">
            Discover the science-backed benefits of daily sunscreen use for cancer prevention, 
            anti-aging, and overall skin health.
          </p>
        </div>
      </div>
      
      {/* Benefits of Sunscreen Section */}
      <div className="facts-section section-container">
        <div className="max-width">
          <span className="beforeHeading">THE SCIENCE</span>
          <h2 className="section-title">Benefits of Sunscreen</h2>
          
          <div className="facts-grid">
            <div className="fact-card">
              <h3>Cancer Prevention</h3>
              <p>
                Regular daily use of an SPF 15 or higher sunscreen can cut your 
                <strong className="highlight"> melanoma risk by about 50% </strong> 
                and lower squamous cell carcinoma risk by up to 40% when used as directed.
              </p>
              <span className="source-text">Sources: Skin Cancer Foundation; Harvard Health Publishing</span>
            </div>
            
            <div className="fact-card">
              <h3>Anti-Aging Power</h3>
              <p>
                Sunscreen blocks both UVA (aging) and UVB (burning) rays, 
                <strong className="highlight"> dramatically reducing the appearance of wrinkles, 
                dark spots, and premature sagging</strong>.
              </p>
              <span className="source-text">Sources: Harvard Health Publishing; The New York Times</span>
            </div>
            
            <div className="fact-card">
              <h3>Sunburn Shield</h3>
              <p>
                By preventing sunburns—which are 
                <strong className="highlight"> linked to increased skin cancer risk</strong>—sunscreen 
                protects your skin's overall health.
              </p>
              <span className="source-text">Sources: Skin Cancer Foundation; American Cancer Society</span>
            </div>
            
            <div className="fact-card">
              <h3>Reduction of Oxidative Damage</h3>
              <p>
                Sunscreen <strong className="highlight">minimizes free radical formation</strong> that can 
                damage DNA and speed up photoaging.
              </p>
              <span className="source-text">Sources: Advanced Pharmaceutical Bulletin; Harvard Health Publishing</span>
            </div>
            
            <div className="fact-card">
              <h3>Reapplication is Key</h3>
              <p>
                For optimal protection, <strong className="highlight">reapply sunscreen every two hours </strong> 
                (or every 40–80 minutes if you're swimming or sweating) to ensure continuous coverage.
              </p>
              <span className="source-text">Sources: Harvard Health Publishing; Skin Cancer Foundation</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Risks of Skin Cancer Section */}
      <div className="facts-section section-container alternate-bg">
        <div className="max-width">
          <span className="beforeHeading">THE RISKS</span>
          <h2 className="section-title">Risks of Skin Cancer</h2>
          
          <div className="facts-grid">
            <div className="fact-card">
              <h3>Widespread Impact</h3>
              <p>
                <strong className="highlight">One in five Americans will develop skin cancer by age 70</strong>, 
                with more than 9,500 new cases diagnosed every day.
              </p>
              <span className="source-text">Sources: Skin Cancer Foundation; American Cancer Society</span>
            </div>
            
            <div className="fact-card">
              <h3>Deadly Melanoma</h3>
              <p>
                Although melanoma represents only 5% of skin cancers, it is responsible for 
                <strong className="highlight"> 71% of all skin cancer deaths</strong>.
              </p>
              <span className="source-text">Source: Skin Cancer Foundation</span>
            </div>
            
            <div className="fact-card">
              <h3>Youth at Risk</h3>
              <p>
                Melanoma is the <strong className="highlight">second most common cancer among 15–29-year-olds</strong>, 
                highlighting the need for sun protection even for younger skin.
              </p>
              <span className="source-text">Source: Skin Cancer Foundation</span>
            </div>
            
            <div className="fact-card">
              <h3>Sunburn Accumulation</h3>
              <p>
                Experiencing <strong className="highlight">five or more sunburns in your life can double your risk</strong> for developing melanoma later on.
              </p>
              <span className="source-text">Sources: Skin Cancer Foundation; Harvard Health Publishing</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Benefits of Moisturizing Section */}
      <div className="facts-section section-container">
        <div className="max-width">
          <span className="beforeHeading">HEALTHY SKIN</span>
          <h2 className="section-title">Benefits of Moisturizing</h2>
          
          <div className="facts-grid">
            <div className="fact-card">
              <h3>Hydration & Barrier Strength</h3>
              <p>
                Moisturizers—especially those containing hyaluronic acid—help 
                <strong className="highlight"> lock in moisture, strengthen your skin's barrier</strong>, 
                and prevent dryness.
              </p>
              <span className="source-text">Sources: Advanced Pharmaceutical Bulletin; Dermatology Care of Charlotte</span>
            </div>
            
            <div className="fact-card">
              <h3>Smooth & Youthful Skin</h3>
              <p>
                Regular moisturization <strong className="highlight">improves skin texture and elasticity</strong>, 
                reducing fine lines and the appearance of wrinkles.
              </p>
              <span className="source-text">Sources: Harvard Health Publishing; Advanced Pharmaceutical Bulletin</span>
            </div>
            
            <div className="fact-card">
              <h3>Enhanced Skin Health</h3>
              <p>
                By maintaining hydration, moisturizers <strong className="highlight">keep your skin supple</strong> and 
                better able to fend off environmental irritants.
              </p>
              <span className="source-text">Sources: American Cancer Society; Harvard Health Publishing</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Environmental Impact Section */}
      <div id="environmental-impact" className="facts-section section-container alternate-bg">
        <div className="max-width">
          <span className="beforeHeading">ENVIRONMENTAL CONCERNS</span>
          <h2 className="section-title">Environmental Impact of Sunscreen</h2>
          
          <div className="facts-grid">
            <div className="fact-card">
              <h3>Single-Use Packaging Waste</h3>
              <p>
                The cosmetics industry creates <strong className="highlight">120 billion packaging units yearly</strong>, with 
                an estimated 14,000 tons of sunscreen chemicals entering waterways worldwide annually.
              </p>
              <span className="source-text">Sources: The Industry Beauty; Ocean Conservancy</span>
            </div>
            
            <div className="fact-card">
              <h3>Recycling Challenges</h3>
              <p>
                <strong className="highlight">Over 90% of plastic waste is never recycled</strong>. Multi-layer 
                sunscreen tubes are especially difficult to recycle, contributing to long-term pollution.
              </p>
              <span className="source-text">Sources: The Industry Beauty; Winkizinc.com.au</span>
            </div>
            
            <div className="fact-card">
              <h3>Coral Reef Damage</h3>
              <p>
                At just 62 parts per trillion, oxybenzone can <strong className="highlight">cause coral bleaching, DNA damage, 
                and deformities in baby corals</strong>, preventing reef regeneration.
              </p>
              <span className="source-text">Sources: Ocean Conservancy; Ocean Service NOAA</span>
            </div>
            
            <div className="fact-card">
              <h3>Marine Life Effects</h3>
              <p>
                Sunscreen chemicals disrupt <strong className="highlight">entire marine ecosystems</strong>, from inhibiting 
                phytoplankton growth to causing hormone disruption in fish and accumulating in dolphin tissues.
              </p>
              <span className="source-text">Sources: Ocean Service NOAA; Smithsonian Ocean</span>
            </div>
            
            <div className="fact-card">
              <h3>Regulatory Response</h3>
              <p>
                <strong className="highlight">Hawaii, Palau, U.S. Virgin Islands, and Aruba</strong> have banned sunscreens 
                containing oxybenzone and octinoxate to protect their vulnerable coral reef ecosystems.
              </p>
              <span className="source-text">Sources: Smithsonian Ocean; Ocean Conservancy</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Overall Reasons Section */}
      <div className="facts-section section-container alternate-bg">
        <div className="max-width">
          <span className="beforeHeading">THE BOTTOM LINE</span>
          <h2 className="section-title">Overall Reasons to Wear Sunscreen</h2>
          
          <div className="facts-grid">
            <div className="fact-card">
              <h3>Complete UV Defense</h3>
              <p>
                Sunscreen is your <strong className="highlight">first line of defense</strong>—blocking both UVA and UVB rays, 
                it forms an essential part of a comprehensive sun protection regimen that includes shade, 
                protective clothing, and sunglasses.
              </p>
              <span className="source-text">Sources: Skin Cancer Foundation; FDA guidelines</span>
            </div>
            
            <div className="fact-card">
              <h3>Prevents Cumulative Damage</h3>
              <p>
                Since sun damage is cumulative, <strong className="highlight">daily sunscreen use can prevent long-term harm</strong>, 
                significantly reducing both skin aging and the risk of skin cancer.
              </p>
              <span className="source-text">Sources: Skin Cancer Foundation; Harvard Health Publishing</span>
            </div>
            
            <div className="fact-card">
              <h3>Synergistic Skincare</h3>
              <p>
                Many modern products <strong className="highlight">combine moisturizing with sun protection</strong>, 
                offering anti-aging benefits and streamlined daily care.
              </p>
              <span className="source-text">Sources: Advanced Pharmaceutical Bulletin; various cosmetic studies</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Call to Action Section */}
      <div className="facts-cta-section section-container">
        <div className="max-width">
          <div className="cta-banner">
            <span className="beforeHeading">PROTECTION MATTERS</span>
            <h2>Start Protecting Your Skin Today</h2>
            <p>
              With Block Automation's sunscreen dispensers, protecting your skin has never been easier. 
              Find a dispenser near you or become a partner to bring this essential service to your location.
            </p>
            <div className="cta-buttons">
              <button 
                className="button button-primary"
                onClick={() => window.location.href = '/partners'}
              >
                Become a Partner
              </button>
              <EmailSignupButton />
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  )
}

export default SunscreenFacts